import React, { useRef, useState, useCallback, useMemo, useImperativeHandle, forwardRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageLayout } from '../../assets/contexts/PageLayoutContext';
import { resolveActiveTopMenuIndex } from '../../assets/utils/comm/activeTopMenuFromBreadcrumb';
import {
  resolveDropdownChildTargetUrl,
  resolveTopMenuItemClickUrl,
} from './menuNavUtils';
import { useFixedWithMoreLayout } from './useFixedWithMoreLayout';

/**
 * 상단 메뉴 + 전형적인 드롭다운 하위 메뉴
 * 메가메뉴와 동일한 menuData를 사용하며, 1뎁스 항목 hover/클릭 시 해당 항목 하단에 2뎁스만 리스트로 표시
 */
const TopNavDropdownMenu = forwardRef(({ menuData, themeConfig = {} }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bannerData } = usePageLayout();
  const containerRef = useRef(null);
  const [openIndex, setOpenIndex] = useState(null);
  /** 메뉴 항목별 드롭다운 우측 정렬(닫힐 때도 유지 — 종료 애니메이션 중 좌↔우 점프 방지) */
  const [dropdownAlignRightByIndex, setDropdownAlignRightByIndex] = useState({});

  const {
    mainMenuNavRef,
    moreContainerRef,
    useFixedWithMore,
    showScrollButtons,
    visibleCountToUse,
    visibleItems,
    overflowItems,
    moreOpen,
    setMoreOpen,
    setNeedReduceForLogo,
  } = useFixedWithMoreLayout(menuData, themeConfig);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreContainerRef.current && !moreContainerRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [moreOpen, moreContainerRef, setMoreOpen]);

  useLayoutEffect(() => {
    if (!useFixedWithMore || !moreOpen || !containerRef.current || !mainMenuNavRef.current) return;
    const headerBottom = containerRef.current.closest('.header-bottom');
    const logoEl = headerBottom?.querySelector('.logo');
    const ul = mainMenuNavRef.current.querySelector(':scope > ul.main-menu-list');
    const firstLi = ul?.querySelector(':scope > li:first-child');
    if (!logoEl || !firstLi) return;
    const logoRect = logoEl.getBoundingClientRect();
    const firstRect = firstLi.getBoundingClientRect();
    const gap = 8;
    const overlaps = firstRect.left < logoRect.right + gap;
    setNeedReduceForLogo((prev) => (overlaps ? true : prev));
  }, [useFixedWithMore, moreOpen, visibleCountToUse, mainMenuNavRef, setNeedReduceForLogo]);

  useImperativeHandle(ref, () => ({
    mainMenuRef: containerRef.current,
    mainMenuNavRef: mainMenuNavRef.current
  }));

  const convertToReactRoute = useCallback((href) => {
    if (!href || href === '#') return null;
    if (/^(https?:)?\/\//.test(href)) return null;
    return href;
  }, []);

  const navigateToHref = useCallback((href) => {
    setOpenIndex(null);
    setMoreOpen(false);
    const route = convertToReactRoute(href);
    if (route) {
      navigate(route);
    } else if (href) {
      window.location.href = href;
    }
  }, [navigate, convertToReactRoute, setMoreOpen]);

  const handleLinkClick = useCallback((e, href) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    navigateToHref(href);
  }, [navigateToHref]);

  const handleParentItemClick = useCallback((e, item, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    const targetUrl = resolveTopMenuItemClickUrl(item);
    if (targetUrl) {
      navigateToHref(targetUrl);
      return;
    }
    setOpenIndex((prev) => (prev === index ? null : index));
  }, [navigateToHref]);

  // 클릭 외부 시 드롭다운 닫기
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  // 열린 드롭다운이 container를 벗어나면 우측 정렬(항목별로 저장, 닫을 때 초기화하지 않음)
  const updateDropdownAlign = useCallback(() => {
    if (openIndex === null) return;
    const container = containerRef.current?.closest('.container');
    const dropdown = containerRef.current?.querySelector('.main-menu-item.dropdown-open .top-nav-dropdown');
    const menuItem = dropdown?.closest('.main-menu-item');
    if (!container || !dropdown || !menuItem) return;
    // 더보기 드롭다운 내부는 CSS로 좌측 fly-out 고정 — align-right 적용 시 항목 위에 겹침
    if (menuItem.closest('.more-dropdown-list')) {
      setDropdownAlignRightByIndex((prev) => {
        if (prev[openIndex] === false) return prev;
        return { ...prev, [openIndex]: false };
      });
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const itemRect = menuItem.getBoundingClientRect();
    const dropdownWidth = dropdown.offsetWidth || dropdown.getBoundingClientRect().width;
    const wouldOverflowRight = itemRect.left + dropdownWidth > containerRect.right;
    setDropdownAlignRightByIndex((prev) => {
      if (prev[openIndex] === wouldOverflowRight) return prev;
      return { ...prev, [openIndex]: wouldOverflowRight };
    });
  }, [openIndex]);

  useLayoutEffect(() => {
    updateDropdownAlign();
  }, [openIndex, updateDropdownAlign]);

  useEffect(() => {
    if (openIndex === null) return;
    const container = containerRef.current?.closest('.container');
    if (!container) return;
    const ro = new ResizeObserver(updateDropdownAlign);
    ro.observe(container);
    window.addEventListener('resize', updateDropdownAlign);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateDropdownAlign);
    };
  }, [openIndex, updateDropdownAlign]);

  const activeTopMenuIndex = useMemo(
    () => resolveActiveTopMenuIndex(menuData, location.pathname, bannerData?.breadcrumbPath),
    [menuData, location.pathname, bannerData?.breadcrumbPath]
  );

  const renderItem = useCallback((item, index, options = {}) => {
    const { inMoreDropdown = false } = options;
    const href = item.href && item.href !== '#' ? item.href : null;
    const route = convertToReactRoute(href);
    const hasChildren = item.children && item.children.length > 0;
    const isActive = activeTopMenuIndex === index;
    const isOpen = openIndex === index;
    const alignDropdownRight = dropdownAlignRightByIndex[index] === true;
    const parentClickUrl = hasChildren ? resolveTopMenuItemClickUrl(item) : null;
    const parentClickRoute = convertToReactRoute(parentClickUrl || href);

    return (
      <li
        key={index}
        className={`main-menu-item ${hasChildren ? 'has-dropdown' : ''} ${isOpen ? 'dropdown-open' : ''} ${isActive ? 'active' : ''}`}
        data-menu-index={index}
        onMouseEnter={() => hasChildren && setOpenIndex(index)}
        onMouseLeave={() => setOpenIndex(null)}
      >
        {hasChildren ? (
          <a
            href={parentClickRoute || parentClickUrl || '#'}
            onClick={(e) => handleParentItemClick(e, item, index)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {item.label}
          </a>
        ) : route ? (
          <a
            href={route}
            onClick={(e) => { handleLinkClick(e, href); setMoreOpen(false); }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {item.label}
          </a>
        ) : href ? (
          <a href={href} onClick={() => setMoreOpen(false)}>{item.label}</a>
        ) : (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid -- 메뉴 항목(링크 없음)은 클릭만 처리
          <a href="#" onClick={(e) => e.preventDefault()}>{item.label}</a>
        )}
        {hasChildren && (
          <div className={`top-nav-dropdown ${alignDropdownRight ? 'dropdown-align-right' : ''}`}>
            <ul className="top-nav-dropdown-list">
              {item.children.map((child, childIndex) => {
                const targetUrl = resolveDropdownChildTargetUrl(child);
                const childRoute = convertToReactRoute(targetUrl);
                return (
                  <li key={childIndex} className="top-nav-dropdown-item">
                    {targetUrl && targetUrl !== '#' ? (
                      childRoute ? (
                        <a href={childRoute} className="top-nav-dropdown-link" title={inMoreDropdown ? undefined : (child.label || undefined)} onClick={(e) => { handleLinkClick(e, targetUrl); setMoreOpen(false); }} onMouseDown={(e) => e.stopPropagation()}>{child.label}</a>
                      ) : (
                        <a href={targetUrl} className="top-nav-dropdown-link" title={inMoreDropdown ? undefined : (child.label || undefined)} onClick={(e) => { handleLinkClick(e, targetUrl); setMoreOpen(false); }} onMouseDown={(e) => e.stopPropagation()}>{child.label}</a>
                      )
                    ) : (
                      <span className="top-nav-dropdown-link top-nav-dropdown-link--disabled" title={inMoreDropdown ? undefined : (child.label || undefined)}>{child.label}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </li>
    );
  }, [openIndex, dropdownAlignRightByIndex, activeTopMenuIndex, convertToReactRoute, handleLinkClick, handleParentItemClick, setMoreOpen]);

  if (!menuData || menuData.length === 0) {
    return (
      <div className="main-menu text-center" ref={containerRef}>
        <nav>
          <ul className="main-menu-list">
            <li className="main-menu-item"><a href="/">HOME</a></li>
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div className="main-menu main-menu--dropdown text-center" ref={containerRef}>
      {showScrollButtons ? (
        <button
          type="button"
          className="main-menu-scroll-btn main-menu-scroll-btn-left"
          aria-label="왼쪽으로 스크롤"
        >
          <i className="pe-7s-angle-left"></i>
        </button>
      ) : null}
      <nav ref={mainMenuNavRef}>
        <ul className="main-menu-list">
          {visibleItems.map((item, index) => renderItem(item, index))}
          {useFixedWithMore && overflowItems.length > 0 && (
            <li
              className={`main-menu-item main-menu-more${activeTopMenuIndex != null && activeTopMenuIndex >= visibleCountToUse ? ' main-menu-more--active-in-dropdown' : ''}`}
              ref={moreContainerRef}
            >
              <button
                type="button"
                className="main-menu-more-trigger"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMoreOpen((prev) => !prev);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                aria-expanded={moreOpen}
                aria-haspopup="true"
                aria-label="더보기"
              >
                <i className="pe-7s-angle-down" aria-hidden="true" />
              </button>
              <div className={`more-dropdown ${moreOpen ? 'is-open' : ''}`}>
                <ul className="more-dropdown-list main-menu-list">
                  {overflowItems.map((item, i) => renderItem(item, visibleCountToUse + i, { inMoreDropdown: true }))}
                </ul>
              </div>
            </li>
          )}
        </ul>
      </nav>
      {showScrollButtons ? (
        <button
          type="button"
          className="main-menu-scroll-btn main-menu-scroll-btn-right"
          aria-label="오른쪽으로 스크롤"
        >
          <i className="pe-7s-angle-right"></i>
        </button>
      ) : null}
    </div>
  );
});

TopNavDropdownMenu.displayName = 'TopNavDropdownMenu';

export default TopNavDropdownMenu;
